import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    Checkbox,
    Flex,
    Label,
    LabeledControl,
    Popper,
    Spacer,
    Text,
    Tooltip,
    TextButton,
} from '@zillow/constellation';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import { RMWContext } from 'app/shared/providers/RMWContext';
import { PropertiesSelectors, UserSelectors } from 'app/shared/selectors';
import ModalActions from 'app/shared/actions/ModalActions';

const tooltipText = `This cannot be changed after the property has been created.
If you want to change how the property is listed you will need to `;

// eslint-disable-next-line max-len
const listingDeclinedUrl = 'https://help.zillowrentalmanager.com/hc/en-us/articles/217482388-Why-was-my-listing-Declined';

const propTypes = {
    isRoomForRentChecked: PropTypes.bool.isRequired,
    isRoomForRentDisabled: PropTypes.bool.isRequired,
    toggleRoomForRentChecked: PropTypes.func,
};

const defaultProps = {
    toggleRoomForRentChecked: null,
};

const RoomForRentCheckbox = ({
    isRoomForRentChecked,
    isRoomForRentDisabled,
    toggleRoomForRentChecked,
}) => {
    const dispatch = useDispatch();
    const { brand } = useContext(RMWContext);
    const { listingAlias } = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));
    const { features: { canAddListing } } = useSelector(state => UserSelectors.makeUser(state));

    const handleAddPropertyClick = () => {
        if (canAddListing) {
            dispatch(ModalActions.hideModal());
            dispatch(ModalActions.updateDisplayAddPropertyModal({ isOpen: false }));

            setTimeout(() => {
                dispatch(ModalActions.updateDisplayAddPropertyModal({
                    isOpen: true,
                    behavior: 'Add',
                }));
            }, 200);
        }

        const dimension = AnalyticsConstants.Z_CUSTOM_DIMENSION.EVENT_CONTENT;
        analyticsUtils.setCustomDimension('RoomForRentToolTipAddProperty', dimension, brand);

        dispatch(analyticsUtils.track.GAEventV2({
            ...AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL,
            ...(listingAlias) && { label: listingAlias },
        }));
    };

    return (
        <Spacer paddingY="xs">
            <Flex display="flex">
                <LabeledControl
                    controlId="LabeledControl-roomForRent"
                    label={<Label>This is a room for rent with a shared living space</Label>}
                    control={(
                        <Checkbox
                            checked={isRoomForRentChecked}
                            onChange={toggleRoomForRentChecked}
                        />
                    )}
                    disabled={isRoomForRentDisabled}
                />
                {isRoomForRentDisabled && (
                    <Popper
                        placement="top"
                        triggered={(
                            <Tooltip
                                shouldCloseOnOutsideClick={true}
                                body={(
                                    <Text fontType="body">
                                        {tooltipText}
                                        <TextButton fontType="body" onClick={handleAddPropertyClick}>
                                            add a new property.
                                        </TextButton>
                                    </Text>
                                )}
                            />
                        )}
                    />
                )}
            </Flex>
            {isRoomForRentChecked && (
                <Text data-test="RR-Notice" fontType="smallbody">
                    Your room for rent listing will not be posted on Zillow.&nbsp;
                    <TextButton fontType="body" as="a" target="_blank" href={listingDeclinedUrl}>
                    Learn more.
                    </TextButton>
                </Text>
            )}
        </Spacer>
    );
};

RoomForRentCheckbox.propTypes = propTypes;
RoomForRentCheckbox.defaultProps = defaultProps;

export default RoomForRentCheckbox;
